<template>
  <div class="registration-form__step registration-form__step-contact-data">
    <div class="registration-form__step__container">
      <div
        v-if="salutationList.length"
        class="salutation-list"
      >
        <label
          v-if="fields.salutationLabel"
          class="required"
        >
          {{ fields.salutationLabel.value }}
        </label>
        <radio-group-fi
          v-model="salutation"
          name="salutation"
          text-attribute="title"
          data-test="salutation-list"
          :data-test-2="salutation"
          :options="salutationListWithUnset"
          required
        />
      </div>

      <div class="data">
        <div
          v-if="titleList.length"
          class="title-list"
        >
          <select-input-fi
            :value="title || null"
            :label="fields.academicTitleLabel?.value"
            :options="titleList"
            data-test="title"
            :data-test-2="title"
            @input="titleChanged"
          />
        </div>

        <text-input-fi
          ref="firstnameRef"
          v-model.trim="firstname"
          class="firstname"
          name="firstname"
          data-test="firstname"
          :rules="rules.firstname"
          :maxlength="120"
          :label="fields.firstNameLabel?.value"
          required
        />

        <text-input-fi
          ref="lastnameRef"
          v-model.trim="lastname"
          name="lastname"
          data-test="lastname"
          :rules="rules.lastname"
          :maxlength="120"
          :label="fields.lastNameLabel?.value"
          required
        />

        <text-input-fi
          ref="emailRef"
          v-model.trim="email"
          name="email"
          data-test="email"
          :rules="
            hasInvalidCharacters || enableEmailValidation
              ? rules.email
              : dynamicRule(rules.email, enableEmailValidationDefault)
          "
          :maxlength="120"
          :label="fields.emailLabel?.value"
          :disabled="registrationLight"
          :required="!registrationLight"
          @keyup="validateCharacters(email, 'email')"
          @keydown="enableEmailValidationDefault = false"
          @blur="emailBlurred"
        />

        <text-input-fi
          id="phone"
          ref="phoneRef"
          v-model.trim="phone"
          name="phone"
          data-test="phone"
          :rules="hasInvalidCharacters || enablePhoneValidation ? rules.phone : []"
          :maxlength="120"
          :label="fields.phoneLabel?.value"
          @keyup="validateCharacters(phone, 'phone')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RadioGroupFi, SelectInputFi, TextInputFi } from 'atoms';
import { useRegistrationStore } from 'store/registration';
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';

import { useFields } from '@/composables/fields';
import { useDict } from '@/composables/fischerPlugin';
import {
  isSalutationValid,
  makeSalutaionListWithUnset,
} from '@/composables/makeSalutationListWithUnset';
import { useValidators } from '@/composables/validators';
import { getSelectList } from '@/utils/fieldTransformations';

defineOptions({ name: 'RegistrationStepContactData' });

const fields = useFields('MyAccountRegisterFields');

const emit = defineEmits<{ 'valid-changed': [payload: boolean] }>();

const dict = useDict();
const { rules, hasInvalidCharacters, validateCharacters, dynamicRule } = useValidators();
const registrationStore = useRegistrationStore();

const firstnameRef = useTemplateRef<InstanceType<typeof TextInputFi>>('firstnameRef');
const lastnameRef = useTemplateRef<InstanceType<typeof TextInputFi>>('lastnameRef');
const emailRef = useTemplateRef<InstanceType<typeof TextInputFi>>('emailRef');
const phoneRef = useTemplateRef<InstanceType<typeof TextInputFi>>('phoneRef');

const isValid = ref(false);
const enableEmailValidation = ref(false);
const enableEmailValidationDefault = ref(true);
const enablePhoneValidation = ref(false);

const registrationLight = computed(() => fields.registrationType?.value === 'Light');

const titleList = computed(() => {
  const [, list] = getSelectList(fields, 'titleList');
  return list || [];
});

const salutationList = computed(() => {
  const [, list] = getSelectList(fields, 'salutationList');
  return list || [];
});

const salutationListWithUnset = computed(() =>
  makeSalutaionListWithUnset(salutationList.value, dict),
);

const salutation = computed({
  get: () => registrationStore.salutation,
  set: (value) => {
    registrationStore.salutation = value;
    validate();
  },
});

const title = computed({
  get: () => registrationStore.title,
  set: (value) => {
    registrationStore.title = value;
  },
});

const firstname = computed({
  get: () => registrationStore.firstname,
  set: (value) => {
    registrationStore.firstname = value;
    validate();
  },
});

const lastname = computed({
  get: () => registrationStore.lastname,
  set: (value) => {
    registrationStore.lastname = value;
    validate();
  },
});

const email = computed({
  get: () => registrationStore.email,
  set: (value) => {
    registrationStore.email = value;
    validate();
  },
});

const phone = computed({
  get: () => registrationStore.phone,
  set: (value) => {
    registrationStore.phone = value;
    validate();
  },
});

const titleChanged = (value: string) => {
  title.value = value;
};

const validate = () => {
  if (firstnameRef.value && lastnameRef.value && emailRef.value && phoneRef.value) {
    const salutationValid = isSalutationValid(
      salutation.value,
      Boolean(salutationList.value.length),
    );

    isValid.value = Boolean(
      salutationValid &&
      firstname.value !== '' &&
      !(firstnameRef.value as any).errorBucket?.length &&
      lastname.value !== '' &&
      !(lastnameRef.value as any).errorBucket?.length &&
      (registrationLight.value ||
        (email.value !== '' &&
          !(emailRef.value as any).errorBucket?.length &&
          enableEmailValidation.value)) &&
      !(phoneRef.value as any).errorBucket?.length,
    );
  } else {
    isValid.value = false;
  }
  emit('valid-changed', isValid.value);
};

const emailBlurred = async () => {
  enableEmailValidation.value = true;
  await nextTick();
  validate();
};

// created logic
if (!salutationList.value.length) {
  salutation.value = [''];
}

if (email.value && !registrationLight.value) {
  enableEmailValidation.value = true;
}

if (phone.value) {
  enablePhoneValidation.value = true;
}

onMounted(() => {
  validate();
});
</script>

<style src="./registration-step-contact-data-unscoped" />

<style scoped src="./registration-step-contact-data" />
